// Implements: F-IDN-009, F-IDN-026
/**
 * "Únete a tu organización" (v3 `vInvitation`): the name and the password of the account an
 * invitation creates. Read-only about the invitation itself (Default P3-1): the server offers no
 * read of an invitation by its token yet, so the page shows no organization, inviter or role.
 * Both fields are required before anything is sent; the server decides the password policy.
 */

import { useState } from "react";
import type { ReactElement, SyntheticEvent } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, errorId } from "../../../shared/ui/molecules/Field";
import styles from "./Organization.module.css";

export interface InvitationAcceptFormProps {
  readonly pending: boolean;
  /** The server's refusal, already in the person's words, or `null`. */
  readonly problem: string | null;
  readonly onSubmit: (fullName: string, password: string) => void;
}

const NAME_ID = "invitation-name";
const PASSWORD_ID = "invitation-password";

export function InvitationAcceptForm({ pending, problem, onSubmit }: InvitationAcceptFormProps): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);

  function submit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;
    const fullName = name.trim();
    const nameMissing = fullName === "";
    const passwordMissing = password === "";
    setNameError(nameMissing ? t("organization.accept.nameRequired") : undefined);
    setPasswordError(passwordMissing ? t("organization.accept.passwordRequired") : undefined);
    if (nameMissing || passwordMissing) return;
    onSubmit(fullName, password);
  }

  return (
    <form className={styles.form} noValidate onSubmit={submit}>
      <Field id={NAME_ID} label={t("organization.accept.name")} error={nameError}>
        <Input
          id={NAME_ID}
          value={name}
          autoComplete="name"
          placeholder={t("organization.accept.namePlaceholder")}
          invalid={nameError !== undefined}
          describedBy={nameError === undefined ? undefined : errorId(NAME_ID)}
          onChange={setName}
        />
      </Field>
      <Field id={PASSWORD_ID} label={t("organization.accept.password")} error={passwordError}>
        <Input
          id={PASSWORD_ID}
          type="password"
          value={password}
          autoComplete="new-password"
          invalid={passwordError !== undefined}
          describedBy={passwordError === undefined ? undefined : errorId(PASSWORD_ID)}
          onChange={setPassword}
        />
      </Field>
      {problem === null ? null : (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
      <Button variant="primary" type="submit" loading={pending}>
        {pending ? t("organization.accept.busy") : t("organization.accept.submit")}
      </Button>
    </form>
  );
}
